import { useState } from 'react';
import { api } from '../lib/api.js';
import { formatDurationShort } from '../lib/format.js';

function isStartedOnDate(isoString, dateKey, tzOffsetMin) {
  if (!isoString || !dateKey) return false;
  const ms = new Date(isoString).getTime();
  // Convert UTC start time into the server's business tz, then format
  // as YYYY-MM-DD so we compare against the same date key the server
  // uses. Falls back to laptop-local if tzOffsetMin isn't supplied.
  const tz = typeof tzOffsetMin === 'number' ? tzOffsetMin : new Date().getTimezoneOffset();
  const local = new Date(ms - tz * 60000);
  const y = local.getUTCFullYear();
  const m = String(local.getUTCMonth() + 1).padStart(2, '0');
  const day = String(local.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}` === dateKey;
}

// Status pills use hardcoded Tailwind colors for the two app-defined
// statuses (To Do = blue, In Progress = cyan) and CSS-var tokens for
// the rest. The hardcoded ones get light/dark variants since their
// dark-mode-friendly defaults (cyan-300, blue-300) wash out on white.
const STATUS_COLOR = {
  'To Do': 'text-blue-700 bg-blue-50 dark:text-blue-300 dark:bg-blue-500/10',
  'In Progress': 'text-cyan-700 bg-cyan-50 dark:text-cyan-300 dark:bg-cyan-500/10',
  'Blocked': 'text-danger bg-danger/10',
  'Complete': 'text-running bg-running/10',
  'On Hold': 'text-warn bg-warn/10'
};

function shortDay(iso) {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

export default function TodayTasks({ stats, runningLog, runningExtraSeconds = 0, onSelect, onRestart, restartingTaskIds }) {
  const [dayOffset, setDayOffset] = useState(0); // 0 = today, -1 = yesterday, etc.
  if (!stats) return null;

  const days = stats.personal?.days || [];
  const todayIndex = days.length - 1;
  const wantedIndex = todayIndex + dayOffset;
  const targetDay = days[wantedIndex];
  const targetDate = targetDay?.date;
  const isToday = dayOffset === 0;

  let tasks;
  // Use the server's authoritative "today" date and tz so this matches
  // the totals computed in /api/my-stats — laptops with wrong tz no
  // longer cause drift.
  const serverToday = stats?.today || (stats?.personal?.days?.[stats.personal.days.length - 1]?.date);
  const runningStartedToday = runningLog?.startTime && serverToday
    ? isStartedOnDate(runningLog.startTime, serverToday, stats?.tzOffsetMin)
    : false;
  if (isToday) {
    tasks = (stats.todayTasks || []).map(t => ({ ...t }));
    if (runningLog?.taskId && runningStartedToday) {
      const existing = tasks.find(t => t.taskId === runningLog.taskId);
      if (existing) {
        existing.seconds = Math.max(existing.seconds, runningExtraSeconds);
        existing.running = true;
      } else {
        tasks.unshift({
          taskId: runningLog.taskId,
          name: runningLog.taskName || runningLog.subTaskName || '(active task)',
          status: 'In Progress',
          seconds: runningExtraSeconds,
          running: true
        });
      }
    }
  } else {
    const dayTasks = (stats.weekByDay && targetDate && stats.weekByDay[targetDate]) || [];
    tasks = dayTasks.map(t => ({
      taskId: t.taskId,
      name: t.name,
      seconds: t.seconds,
      status: ''
    }));
  }

  tasks.sort((a, b) => {
    if (a.running && !b.running) return -1;
    if (b.running && !a.running) return 1;
    return b.seconds - a.seconds;
  });

  const canGoBack = todayIndex + dayOffset - 1 >= 0;
  const canGoForward = dayOffset < 0;

  const handleClick = async taskId => {
    try {
      const d = await api.tasksByIds([taskId]);
      const t = (d.tasks || [])[0];
      if (t) onSelect(t);
    } catch {}
  };

  const headerLabel = isToday
    ? `Today · ${tasks.length} task${tasks.length === 1 ? '' : 's'}`
    : `${shortDay(targetDate || '')} · ${tasks.length} task${tasks.length === 1 ? '' : 's'}`;

  return (
    <div className="rounded-lg border border-border bg-panel overflow-hidden">
      <div className="px-4 py-2 border-b border-border bg-panel2/40 flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider text-muted flex items-center gap-1.5">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-accent">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 6v6l4 2" />
          </svg>
          {headerLabel}
        </div>
        <div className="flex items-center gap-1">
          <button
            disabled={!canGoBack}
            onClick={() => setDayOffset(o => o - 1)}
            title="Previous day"
            className="p-1 rounded hover:bg-panel2 text-muted hover:text-text disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          {!isToday && (
            <button
              onClick={() => setDayOffset(0)}
              className="text-[10px] px-2 py-0.5 rounded text-accent hover:bg-accent/10 font-medium"
            >
              Today
            </button>
          )}
          <button
            disabled={!canGoForward}
            onClick={() => setDayOffset(o => o + 1)}
            title="Next day"
            className="p-1 rounded hover:bg-panel2 text-muted hover:text-text disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
        </div>
      </div>

      {tasks.length === 0 ? (
        <div className="p-6 text-center text-muted text-xs">
          No tasks tracked on this day.
        </div>
      ) : (
        <div className="divide-y divide-border">
          {tasks.map(t => {
            const isRunning = Boolean(t.running);
            const showRestart = !isRunning && (t.status === 'Complete' || !isToday) && onRestart;
            const restarting = Boolean(restartingTaskIds?.[t.taskId]);
            return (
              <div
                key={t.taskId}
                className={`flex items-center gap-2 px-4 py-2.5 hover:bg-panel2 transition-colors group cursor-pointer ${
                  isRunning ? 'bg-running/10 border-l-2 border-running' : ''
                }`}
                onClick={() => handleClick(t.taskId)}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    {isRunning && <div className="w-2 h-2 rounded-full bg-running pulse-dot flex-shrink-0" />}
                    <div className="text-sm font-medium truncate">{t.name}</div>
                  </div>
                  {t.status && (
                    <span className={`inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded font-medium ${STATUS_COLOR[t.status] || 'text-muted bg-panel2'}`}>
                      {t.status}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <div className={`font-mono text-sm ${isRunning ? 'text-running font-semibold' : 'text-text/90'}`}>
                    {formatDurationShort(t.seconds)}
                  </div>
                  {showRestart && (
                    <button
                      onClick={e => { e.stopPropagation(); onRestart(t); }}
                      disabled={restarting}
                      title="Start a fresh instance of this task"
                      className="p-1.5 rounded-md hover:bg-accent/15 border border-transparent hover:border-accent/40 transition-colors text-accent"
                    >
                      {restarting ? (
                        <span className="text-[10px] font-mono px-0.5">...</span>
                      ) : (
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
                          <polygon points="5,3 19,12 5,21" />
                        </svg>
                      )}
                    </button>
                  )}
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    className="text-muted group-hover:text-accent transition-colors"
                  >
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
